import type { EnergyLabel, House, HouseType, LatLng } from '../types/house'
import { ringArea } from './geo'

export const HOUSE_TYPE_LABELS: Record<HouseType, string> = {
  vrijstaand: 'Vrijstaand',
  'twee-onder-een-kap': '2-onder-1-kap',
  hoekwoning: 'Hoekwoning',
  tussenwoning: 'Tussenwoning',
  appartement: 'Appartement',
}

export const HOUSE_TYPES: HouseType[] = [
  'vrijstaand',
  'twee-onder-een-kap',
  'hoekwoning',
  'tussenwoning',
  'appartement',
]

export function labelFromBuildYear(year: number): EnergyLabel {
  if (year < 1946) return 'F'
  if (year < 1965) return 'E'
  if (year < 1975) return 'D'
  if (year < 1992) return 'C'
  if (year < 2006) return 'B'
  if (year < 2015) return 'A'
  if (year < 2021) return 'A+'
  return 'A++'
}

export const LABEL_COLORS: Record<EnergyLabel, string> = {
  'A++++': '#0F7A3D',
  'A+++': '#0F7A3D',
  'A++': '#1B8A3F',
  'A+': '#2E9A3E',
  A: '#4CAF3D',
  B: '#8CC43F',
  C: '#D5CF2E',
  D: '#F2B33D',
  E: '#EC8A2B',
  F: '#E0561F',
  G: '#C8281C',
}

export function guessHouseType(units: number, neighbours: number, footprintArea: number): HouseType {
  if (units > 1) return 'appartement'
  if (neighbours === 0) return 'vrijstaand'
  if (neighbours === 1) return footprintArea > 75 ? 'twee-onder-een-kap' : 'hoekwoning'
  return 'tussenwoning'
}

interface DeriveInput {
  street: string
  houseNumber: string
  postcode: string
  city: string
  lat: number
  lng: number
  footprint: LatLng[]
  buildYear: number
  livingArea?: number
  units: number
  neighbours: number
  source: House['source']
}

export function deriveHouse(input: DeriveInput): House {
  const footprintArea = Math.round(ringArea(input.footprint))
  const houseType = guessHouseType(input.units, input.neighbours, footprintArea)
  const isApartment = houseType === 'appartement'
  const roofArea = isApartment ? footprintArea : Math.round(footprintArea * 1.15)
  const panelPotential = isApartment ? 0 : Math.max(0, Math.min(40, Math.floor((roofArea * 0.5) / 1.95)))
  const livingArea =
    input.livingArea && input.livingArea > 0 ? input.livingArea : Math.round(footprintArea * 1.7)

  return {
    street: input.street,
    houseNumber: input.houseNumber,
    postcode: input.postcode,
    city: input.city,
    lat: input.lat,
    lng: input.lng,
    footprint: input.footprint,
    footprintArea,
    buildYear: input.buildYear,
    livingArea,
    units: input.units,
    energyLabel: labelFromBuildYear(input.buildYear),
    houseType,
    roofArea,
    panelPotential,
    source: input.source,
  }
}

export function formatPostcode(postcode: string): string {
  const clean = postcode.replace(/\s/g, '').toUpperCase()
  return clean.length === 6 ? `${clean.slice(0, 4)} ${clean.slice(4)}` : clean
}

export function isValidPostcode(postcode: string): boolean {
  return /^[1-9]\d{3}\s?[a-zA-Z]{2}$/.test(postcode.trim())
}
